import React, { useState } from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { NotificationToggleProps } from "../NotificationTypes";

export function NotificationToggle({
  id,
  subscriptionId,
  label,
  enabled,
  defaultRecipients,
  onEnable,
  onDisable,
  isReadOnly = false,
}: NotificationToggleProps) {
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [recipients, setRecipients] = useState<string[]>([
    ...defaultRecipients,
  ]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const handleTagClick = () => {
    if (isReadOnly) return;
    if (enabled) {
      setShowDisableModal(true);
    } else {
      setShowEnableModal(true);
    }
  };
  const confirmEnable = async () => {
    setIsProcessing(true);
    setAlertMessage(null);
    try {
      // await the parent’s async onEnable
      await onEnable();
      setAlertMessage("Notifications enabled!");
      // give the user a moment to read it
      setTimeout(() => {
        setShowEnableModal(false);
        setAlertMessage(null);
      }, 2000);
    } catch (err) {
      console.error(err);
      setAlertMessage("Failed to enable notifications.");
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelEnable = () => {
    setShowEnableModal(false);
    setAlertMessage(null);
  };

  const confirmDisable = async () => {
    setIsProcessing(true);
    setAlertMessage(null);
    try {
      await onDisable(id, subscriptionId);
      setAlertMessage("Notifications disabled.");
      setTimeout(() => {
        setShowDisableModal(false);
        setAlertMessage(null);
      }, 2000);
    } catch (err) {
      console.error(err);
      setAlertMessage("Failed to disable notifications.");
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelDisable = () => {
    setShowDisableModal(false);
    setAlertMessage(null);
  };

  return (
    <>
      <GenericTag
        variant={enabled ? "success" : "error"}
        size="small"
        onClick={!isReadOnly ? handleTagClick : undefined}
        label={enabled ? "Enabled" : "Disabled"}
        className={
          isReadOnly
            ? "opacity-50 cursor-default pointer-events-none"
            : "cursor-pointer"
        }
      />
      {/* Enable confirmation modal */}
      {!isReadOnly && (
        <GenericModal
          isOpen={showEnableModal}
          onClose={cancelEnable}
          title={
            <span className="text-lg font-semibold text-semantic-text-primary">
              {`Enable ${label} Notification`}
            </span>
          }
        >
          {/* Intro */}
          <div className="mb-2 text-md text-semantic-text-secondary">
            You are about to enable <strong>{label}</strong> notification for
            the selected service.
          </div>

          {/* Conditional line */}
          {recipients.length > 0 ? (
            <div className="mb-4 text-xs font-medium text-semantic-text-primary">
              The following recipients will receive notifications:
            </div>
          ) : (
            <div className="mb-4 text-xs font-medium text-semantic-text-tertiary">
              You can only add email addresses from your organization that are
              not already associated with an existing user account. Only you
              will receive notifications.
            </div>
          )}
          {/* Inline alert message */}
          {alertMessage && (
            <div className="p-2 mb-3 text-sm text-center rounded border border-gray-200 bg-gray-50">
              {alertMessage}
            </div>
          )}

          {/* <DefaultRecipientsManager
          initialRecipients={recipients}
          onRecipientsChange={setRecipients}
          showControls={false}
        /> */}
          <div className="flex justify-end space-x-2">
            <GenericButton
              size="sm"
              variant="secondary"
              onClick={cancelEnable}
              label="Cancel"
              disabled={isProcessing}
            />
            <GenericButton
              size="sm"
              onClick={confirmEnable}
              label="Confirm"
              loading={isProcessing}
              disabled={isProcessing}
            />
          </div>
        </GenericModal>
      )}

      {/* Disable confirmation modal */}
      {!isReadOnly && (
        <GenericModal
          isOpen={showDisableModal}
          onClose={cancelDisable}
          title={
            <span className="text-lg font-semibold text-semantic-text-primary">
              {`Disable ${label} Notification`}
            </span>
          }
        >
          {alertMessage && (
            <div className="p-2 mb-3 text-sm text-center rounded border border-gray-200 bg-gray-50">
              {alertMessage}
            </div>
          )}
          <p className="text-md text-semantic-text-secondary mb-4">
            Are you sure you want to disable <strong>{label}</strong>{" "}
            Notification? <br />
            <span className="mb-4 text-xs font-medium text-semantic-text-tertiary">
              This action will stop sending alerts for the selected service.
            </span>
          </p>
          <div className="flex justify-end space-x-2">
            <GenericButton
              size="sm"
              variant="secondary"
              onClick={cancelDisable}
              label="Cancel"
              disabled={isProcessing}
            />
            <GenericButton
              variant="brand"
              size="sm"
              onClick={confirmDisable}
              label="Confirm"
              loading={isProcessing}
              disabled={isProcessing}
            />
          </div>
        </GenericModal>
      )}
    </>
  );
}

export default NotificationToggle;
